import React, { useEffect, useState, useRef } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useOutletContext, useNavigate, useParams } from "react-router-dom";
import {
  BackArrowIcon,
  CompanyInfoIcon,
  addNewTrentIcon,
  PlansModulesIcon,
  ConfigurationIcon,
  ReviewCreateIcon,
} from "../../assets/icons";
import CompanyInfo from "./CompanyInfo";
import AdminSetup from "./AdminSetup";
import PlansModules from "./PlansModules";
import Configuration from "./Configuration";
import ReviewCreate from "./ReviewCreate";
import {
  reqToGetmodulesList,
  reqToGetPlanList,
} from "../../redux/slices.js/plans/plansSlice";
import { useDispatch, useSelector } from "react-redux";
import { routes } from "../../helpers/constants";
import {
  reqToAddTenant,
  reqToEditTenant,
  reqToGetCountries,
  reqToGetTenantDetails,
} from "../../redux/slices.js/tenants/tenantsSlice";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";

const steps = [
  {
    id: 1,
    label: "Company info",
    icon: CompanyInfoIcon,
    component: CompanyInfo,
  },
  {
    id: 2,
    label: "Admin set up",
    icon: addNewTrentIcon,
    component: AdminSetup,
  },
  {
    id: 3,
    label: "Plans & Modules",
    icon: PlansModulesIcon,
    component: PlansModules,
  },
  {
    id: 4,
    label: "Configuration",
    icon: ConfigurationIcon,
    component: Configuration,
  },
  {
    id: 5,
    label: "Review & Create",
    icon: ReviewCreateIcon,
    component: ReviewCreate,
  },
];

// Validation Schemas for each step
const stepValidations = [
  Yup.object().shape({
    companyName: Yup.string().required("Company Name is required"),
    industry: Yup.object().required("Industry is required"),
    companyEmail: Yup.string()
      .email("Invalid email")
      .required("Company Email is required"),
    phoneNumber: Yup.string().required("Phone Number is required"),
    employeesCount: Yup.number()
      .required("Number of employees is required")
      .min(1, "Must be at least 1"),
    country: Yup.number()
      .typeError("Country is required")
      .required("Country is required"),

    companyAddress: Yup.string(),
  }),
  Yup.object().shape({
    adminFirstName: Yup.string().required("First Name is required"),
    adminLastName: Yup.string().required("Last Name is required"),
    adminEmail: Yup.string()
      .email("Invalid email")
      .required("Admin Email is required"),
    adminPhone: Yup.string().required("Admin Phone is required"),
    requirePasswordReset: Yup.boolean(),
    sendWelcomeEmail: Yup.boolean(),
  }),
  Yup.object().shape({
    selectedPlan: Yup.string().required("Please select a plan"),
    enabledModules: Yup.array().min(1, "Please select at least one module"),
    billingCycle: Yup.string().required("Please select a billing cycle"),
  }),
  Yup.object().shape({
    supportOwner: Yup.number()
      .typeError("Support owner is required")
      .required("Support owner is required"),
    onboardingSpecialist: Yup.number()
      .typeError("Onboarding specialist is required")
      .required("Onboarding specialist is required"),
    dataRetentionPeriod: Yup.number()
      .required("Data retention period is required")
      .min(1, "Must be at least 1 day"),
    internalNotes: Yup.string(),
    enableSSO: Yup.boolean(),
    enableAPI: Yup.boolean(),
    enableBranding: Yup.boolean(),
  }),

  Yup.object().shape({}),
];

const initialValues = {
  // Step 1
  companyName: "",
  industry: null,
  companyEmail: "",
  phoneNumber: "",
  employeesCount: "",
  country: null,
  companyAddress: "",

  // Step 2
  adminFirstName: "",
  adminLastName: "",
  adminEmail: "",
  adminPhone: "",
  requirePasswordReset: false,
  sendWelcomeEmail: false,

  // Step 3
  selectedPlan: "",
  enabledModules: [],
  billingCycle: "monthly",

  // Step 4
  supportOwner: null,
  onboardingSpecialist: null,
  dataRetentionPeriod: 365,
  internalNotes: "",
  enableSSO: false,
  enableAPI: false,
  enableBranding: false,
};

const Index = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isViewMode = searchParams.get("tab") === "review";
  const [currentStep, setCurrentStep] = useState(isViewMode ? 5 : 1);
  const { isSidebarOpen } = useOutletContext();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState();
  const [countries, setCountries] = useState();
  const dispatch = useDispatch();

  const tenant = useSelector((state) => state.tenants.tenantDeatils); // tenant for edit

  const isEditMode = !!id;

  const [initialFormValues, setInitialFormValues] = useState(initialValues);

  // Fetch tenant details if edit mode
  useEffect(() => {
    if (isEditMode) {
      dispatch(
        reqToGetTenantDetails({
          id: Number(id),
          onSuccess: () => console.log("Tenant fetched successfully"),
          onFailure: (err) =>
            console.error("Failed to fetch tenant data:", err),
        })
      );
    }
  }, [dispatch, id, isEditMode]);

  // Update initialValues when tenant data is loaded
  useEffect(() => {
    if (isEditMode && tenant) {
      setInitialFormValues({
        companyName: tenant?.tenant?.name || "",
        industry: tenant?.tenant?.industry
          ? { value: tenant?.tenant?.industry, label: tenant?.tenant?.industry }
          : null,
        companyEmail: tenant?.tenant?.email || "",
        phoneNumber: tenant?.tenant?.phone || "",
        employeesCount: tenant?.tenant?.team_strength?.toString() || "", //it's total employee need to chnage active employee
        country: tenant?.tenant?.country || null,
        companyAddress: tenant?.tenant?.company_address || "",

        adminFirstName: tenant?.name?.split(" ")[0] || "",
        adminLastName: tenant?.name?.split(" ")[1] || "",
        adminEmail: tenant?.email || "",
        adminPhone: tenant?.phone || "",
        requirePasswordReset: tenant?.tenant?.require_password_reset || false, //pending
        sendWelcomeEmail: tenant?.tenant?.send_welcome_email || false, //pending

        selectedPlan: tenant?.tenant?.subscription || "",
        enabledModules: tenant?.tenant?.modules || [], //pending
        billingCycle: tenant?.tenant?.billing_cycle || "monthly",

        supportOwner: tenant?.tenant?.support_ownwer || null,
        onboardingSpecialist: tenant?.tenant?.onborading_specialist || null,
        dataRetentionPeriod: tenant?.tenant?.data_retention_period || 365,
        internalNotes: tenant?.tenant?.notes || "",
        enableSSO: tenant?.tenant?.enable_sso || false, //pending
        enableAPI: tenant?.tenant?.enable_api || false, //pending
        enableBranding: tenant?.tenant?.custom_branding || false,
      });
    }
  }, [tenant, isEditMode]);

  // Fetch plans, modules, countries (same as before)
  useEffect(() => {
    dispatch(
      reqToGetPlanList({
        data: {},
        onSuccess: (res) => setPlans(res?.data || []),
        onFailure: (err) => console.error("Failed to fetch plans:", err),
      })
    );
    dispatch(
      reqToGetmodulesList({
        data: {},
        onSuccess: (res) => setModules(res?.data || []),
        onFailure: (err) => console.error("Failed to fetch modules:", err),
      })
    );
    dispatch(
      reqToGetCountries({
        search: "",
        onSuccess: (res) => setCountries(res?.data || []),
        onFailure: (err) => console.error(err),
      })
    );
  }, [dispatch]);

  const handleNext = (validateForm, setTouched) => {
    // Mark all fields as touched to show validation errors
    const currentStepFields = Object.keys(
      stepValidations[currentStep - 1].fields
    );
    const touchedObj = {};
    currentStepFields.forEach((field) => {
      touchedObj[field] = true;
    });
    setTouched(touchedObj);

    validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        setCurrentStep((prev) => prev + 1);
      } else {
      }
    });
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // Form submit logic
  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [currentStep]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const apiData = {
      admin_email: values.adminEmail,
      admin_first_name: values.adminFirstName,
      admin_last_name: values.adminLastName,
      company_name: values.companyName,
      industry: values.industry?.value,
      team_strength: values.employeesCount?.toString(),
      company_email: values.companyEmail,
      company_phone: values.phoneNumber,
      country: values.country,
      company_address: values.companyAddress,
      admin_phone: values.adminPhone,
      require_password_reset: values.requirePasswordReset,
      send_welcome_email: values.sendWelcomeEmail,
      plan_id: values.selectedPlan,
      modules: values.enabledModules,
      billing_cycle: values.billingCycle,
      support_owner: values.supportOwner,
      onboarding_specialist: values.onboardingSpecialist,
      data_retention_period: values.dataRetentionPeriod,
      notes: values.internalNotes,
      enable_sso: values.enableSSO,
      enable_api: values.enableAPI,
      custom_branding: values.enableBranding,
    };

    try {
      if (isEditMode) {
        // Call update tenant API
        await dispatch(
          reqToEditTenant({
            id: Number(id),
            data: apiData,
            onSuccess: () => {
              toast.success("Tenant updated successfully!");
              setSubmitting(false);
              navigate(routes.tenants);
            },
            onFailure: () => setSubmitting(false),
          })
        );
      } else {
        // Call create tenant API
        await dispatch(
          reqToAddTenant({
            data: apiData,
            onSuccess: () => {
              toast.success("Tenant created successfully!");
              resetForm();
              setSubmitting(false);
              navigate(routes.tenantAddedSuccess);
            },
            onFailure: () => setSubmitting(false),
          })
        );
      }
    } catch (err) {
      console.error("Error:", err);
      setSubmitting(false);
    }
  };

  const CurrentStepComponent = steps[currentStep - 1]?.component;
  const formRef = useRef(null);

  // Scroll to top of form when step changes
  useEffect(() => {
    if (formRef.current) {
      formRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [currentStep]);

  return (
    <div className="rounded-md border-[1px] border-solid border-[#F3EFEF] sm:pb-6 h-full">
      <div className="p-3 sm:p-6 bg-[#F5FFBD] space-y-6">
        <div className="mb-4">
          <button
            type="button"
            className="text-[16px] font-normal flex gap-1"
            onClick={() => navigate(-1)}
          >
            {BackArrowIcon} Back
          </button>
        </div>
        <div className="heading flex gap-2 justify-between items-end">
          <div className="heading-title space-y-1">
            <h1 className="text-h2 !font-medium mb-0 text-[#4285F4]">
              Provision New Tenant
            </h1>
            <p className="text-span text-dark mb-0">
              Create and Configure a new tenant organisation
            </p>
          </div>
        </div>

        <ol className="flex items-center gap-2 step-wise">
          {steps.map((step, index) => {
            const isCurrent = step.id === currentStep;
            const isCompleted = step.id < currentStep;
            const circleClasses = [
              "flex items-center justify-center transition label-svg",
              isCompleted
                ? "blueColorBtn"
                : isCurrent
                  ? "blueColorBtn"
                  : "grayColorBtn",
            ].join(" ");

            const labelClasses = [
              "text-span whitespace-nowrap label-size",
              isCurrent || isCompleted ? "text-[#4285F4]" : "text-[#9BA2AB]",
            ].join(" ");

            const connectorActive = currentStep > step.id;

            return (
              <li
                key={step.id}
                className={`flex items-center ${index !== steps.length - 1 ? "flex-1" : ""
                  }`}
              >
                <div className="flex items-center text-center gap-2">
                  <div className={circleClasses}>{step.icon}</div>
                  <span className={labelClasses}>{step.label}</span>
                </div>

                {index !== steps.length - 1 && (
                  <div
                    className={[
                      "ms-2 h-[2px] flex-1 rounded-full transition",
                      connectorActive ? "bg-[#4285F4]" : "bg-[#D3D6DA3B]",
                    ].join(" ")}
                  />
                )}
              </li>
            );
          })}
        </ol>
      </div>
      <Formik
        initialValues={initialFormValues}
        enableReinitialize
        validationSchema={stepValidations[currentStep - 1]}
        onSubmit={handleSubmit}
        validateOnBlur
        validateOnChange
      >
        {({ validateForm, setTouched, submitForm, isSubmitting, values }) => (
          <Form ref={formRef} className="p-3 pt-6 sm:p-6 comman-form h-[calc(100%-185px)] flex flex-col overflow-y-auto scroll-none">
            {currentStep === 5 ? (
              <ReviewCreate
                formValues={values}
                modulesList={modules}
                plansList={plans}
                countriesList={countries}
              />
            ) : (
              CurrentStepComponent && <CurrentStepComponent />
            )}

            {!isViewMode && (
              <div className={`mt-auto flex gap-3 pt-6 justify-end flex-wrap`}>
                {currentStep > 1 && (
                  <button
                    type="button"
                    className="previous-btn"
                    onClick={() => setCurrentStep(currentStep - 1)}
                    disabled={isSubmitting}
                  >
                    Previous
                  </button>
                )}

                {currentStep === steps.length ? (
                  <button
                    type="button"
                    className="next-btn"
                    onClick={submitForm}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? isEditMode
                        ? "Updating..."
                        : "Creating..."
                      : isEditMode
                        ? "Update Tenant"
                        : "Create Tenant"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="next-btn"
                    onClick={() => handleNext(validateForm, setTouched)}
                    disabled={isSubmitting}
                  >
                    Next
                  </button>
                )}
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default Index;
